import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Stack,
  TextField,
  MenuItem,
  Button,
  Grid,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Pagination,
  Tabs,
  Tab,
} from '@mui/material';
import axiosInstance from '../utils/axios';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';

const statusOptions = [
  { label: 'All', value: '' },
  { label: 'Delivered', value: 'delivered' },
  { label: 'Started', value: 'started' },
  { label: 'Cancelled', value: 'cancelled' },
];

export default function RiderJobs() {
  const [searchParams, setSearchParams] = useSearchParams();
  const scope = searchParams.get('scope') === 'community' ? 'community' : 'mine';
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [loading, setLoading] = useState(false);

  const [status, setStatus] = useState('');
  const [username, setUsername] = useState('');
  const [jobID, setJobID] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const totalPages = useMemo(() => Math.max(1, Math.ceil(total / limit)), [total, limit]);

  const fetchJobs = async () => {
    setLoading(true);
    try {
      const endpoint = scope === 'mine' ? '/me/jobs' : '/jobs/public';
      const { data } = await axiosInstance.get(endpoint, {
        params: {
          page,
          limit,
          status: status || undefined,
          username: scope === 'community' ? (username || undefined) : undefined,
          jobID: jobID || undefined,
          dateFrom: dateFrom || undefined,
          dateTo: dateTo || undefined,
        },
      });
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchJobs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, limit, scope]);

  useEffect(() => {
    setPage(1);
    fetchJobs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, username, jobID, dateFrom, dateTo, scope]);

  const applyFilters = () => {
    setPage(1);
    fetchJobs();
  };

  const setScope = (next) => {
    const params = new URLSearchParams(searchParams);
    if (next === 'community') params.set('scope', 'community');
    else params.delete('scope');
    setSearchParams(params, { replace: true });
    setPage(1);
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', px: 3, py: 4 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Typography variant="h5" fontWeight={700}>
          {scope === 'mine' ? 'My jobs' : 'Community jobs'}
        </Typography>
      </Stack>

      <Tabs
        value={scope}
        onChange={(_, v) => setScope(v)}
        sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab value="mine" label="My deliveries" />
        <Tab value="community" label="Community browse" />
      </Tabs>

      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} md={2}>
              <TextField select label="Status" fullWidth size="small" value={status} onChange={(e) => setStatus(e.target.value)}>
                {statusOptions.map((o) => (
                  <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                ))}
              </TextField>
            </Grid>
            {scope === 'community' && (
              <Grid item xs={12} md={3}>
                <TextField label="Username" fullWidth size="small" value={username} onChange={(e) => setUsername(e.target.value)} />
              </Grid>
            )}
            <Grid item xs={12} md={2}>
              <TextField
                label="Job ID"
                type="number"
                fullWidth
                size="small"
                value={jobID}
                onChange={(e) => setJobID(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                label="From"
                type="date"
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                label="To"
                type="date"
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={1}>
              <Button fullWidth variant="contained" onClick={applyFilters} disabled={loading} sx={{ height: '100%' }}>
                Apply
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Job ID</TableCell>
              <TableCell>Driver</TableCell>
              <TableCell>Route</TableCell>
              <TableCell>Distance</TableCell>
              <TableCell>Revenue</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Date</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((job) => (
              <TableRow key={job._id || job.jobID} hover component={RouterLink} to={`/jobs/${job._id || job.jobID}`} style={{ textDecoration: 'none' }}>
                <TableCell>{job.jobID}</TableCell>
                <TableCell>{job.driver?.username || '—'}</TableCell>
                <TableCell>
                  {(job.source?.city?.name || job.source?.city?.id || (typeof job.source === 'string' ? job.source : '—'))}
                  {' → '}
                  {(job.destination?.city?.name || job.destination?.city?.id || (typeof job.destination === 'string' ? job.destination : '—'))}
                </TableCell>
                <TableCell>{Math.round(job.distanceDriven || 0).toLocaleString()} km</TableCell>
                <TableCell>{Math.round(job.normalized?.revenueValue || job.revenue || job.income || 0).toLocaleString()}</TableCell>
                <TableCell>
                  <Chip size="small" label={job.status || '—'} />
                </TableCell>
                <TableCell>{job.createdAt ? new Date(job.createdAt).toLocaleDateString() : '—'}</TableCell>
              </TableRow>
            ))}
            {!loading && items.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center">No jobs found</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2 }}>
        <Pagination count={totalPages} page={page} onChange={(_, p) => setPage(p)} color="primary" />
      </Stack>
    </Box>
  );
}
